import { cloneDeep } from 'lodash';
import {
    CdsExportAdditionalDocument,
    CdsExportClassificationBE,
    CdsDeclaration,
    CdsGovernmentAgencyGoodsItem,
    CdsExportGovernmentProcedureServer,
} from 'store/declarations/uk/cds-declaration';
import { joinTypeCode, splitTypeCode } from '../common/typeCode';

type CommodityCodeIdentifier = { id: string | undefined };
type ClientCommodityCodes = {
    cv: CommodityCodeIdentifier;
    tsp: CommodityCodeIdentifier;
    trc: CommodityCodeIdentifier;
    tra: CommodityCodeIdentifier[];
    gn: CommodityCodeIdentifier[];
};

type AdditionalDocumentFE = Omit<CdsExportAdditionalDocument, 'categoryType' | 'typeCode'> & { code?: string };

export const transformAdditionalDocumentForClient = (product: CdsGovernmentAgencyGoodsItem | undefined | null) => {
    return product?.additionalDocument?.reduce((acc, document) => {
        const code = (document.categoryCode ?? '') + (document.typeCode ?? '');
        delete document.categoryCode;
        delete document.typeCode;
        const newDocument = cloneDeep(document) as AdditionalDocumentFE;
        newDocument.code = code;
        acc.push(newDocument);
        return acc;
    }, [] as AdditionalDocumentFE[]);
};
export const transformAdditionalDocumentForServer = (product: CdsGovernmentAgencyGoodsItem | undefined | null) => {
    return product?.additionalDocument?.reduce((acc, _document) => {
        const document = cloneDeep(_document) as AdditionalDocumentFE;
        const categoryCode = document.code?.substring(0, 1);
        const typeCode = document.code?.substring(1);
        delete document.code;
        const newDocument = cloneDeep(document) as CdsExportAdditionalDocument;
        newDocument.categoryCode = categoryCode;
        newDocument.typeCode = typeCode;
        acc.push(newDocument);
        return acc;
    }, [] as AdditionalDocumentFE[]);
};

export const transformCommodityCodesForClient = (product: CdsGovernmentAgencyGoodsItem | undefined | null) => {
    return product?.commodity?.classification?.reduce((acc, commodityCode) => {
        const { id, identificationTypeCode } = commodityCode;
        const key = identificationTypeCode?.toLowerCase() as 'cv' | 'tsp' | 'trc' | 'tra' | 'gn';
        switch (identificationTypeCode) {
            case 'CV':
            case 'TSP':
            case 'TRC':
                acc[key as 'cv' | 'tsp' | 'trc'] = { id };
                return acc;
            case 'TRA':
            case 'GN':
                if (!acc[key]) acc[key as 'tra' | 'gn'] = [];
                acc[key as 'tra' | 'gn'].push({ id });
                return acc;
            default:
                return acc;
        }
    }, {} as ClientCommodityCodes);
};
export const transformCommodityCodesForServer = (product: CdsGovernmentAgencyGoodsItem | undefined | null) => {
    const commodityCodes = product?.commodity?.classification as unknown as ClientCommodityCodes | undefined;
    return (
        commodityCodes &&
        Object.entries(commodityCodes).reduce((acc, [key, value]) => {
            switch (key) {
                case 'cv':
                case 'tsp':
                case 'trc':
                    if ((value as CommodityCodeIdentifier | undefined)?.id)
                        acc.push({
                            id: (value as CommodityCodeIdentifier).id,
                            identificationTypeCode: key.toUpperCase(),
                        });
                    return acc;
                case 'tra':
                case 'gn':
                    (value as (CommodityCodeIdentifier | undefined)[]).forEach((v) => {
                        if (v?.id) acc.push({ id: v.id, identificationTypeCode: key.toUpperCase() });
                    });
                    return acc;
                default:
                    return acc;
            }
        }, [] as CdsExportClassificationBE[])
    );
};

class CdsExportDeclarationUtils {
    static addProductToPayload(_cdsDeclaration: CdsDeclaration) {
        const cdsDeclaration = cloneDeep(_cdsDeclaration);
        return {
            ...cdsDeclaration,
            cdsDeclarationPayload: {
                ...cdsDeclaration.cdsDeclarationPayload,
                goodsShipment: {
                    ...cdsDeclaration.cdsDeclarationPayload?.goodsShipment,
                    governmentAgencyGoodsItem: cdsDeclaration.cdsGovernmentAgencyGoodsItems?.map(
                        ({ governmentAgencyGoodsItem }) => governmentAgencyGoodsItem
                    ),
                },
            },
        };
    }

    static transformForClient(_cdsDeclaration: CdsDeclaration) {
        const cdsDeclaration = cloneDeep(_cdsDeclaration);
        const payload = { ...cdsDeclaration.cdsDeclarationPayload };

        const { typeCode, additionalTypeCode } = splitTypeCode(payload?.typeCode);

        const ducr = payload.goodsShipment?.previousDocument?.find((prevDoc) => prevDoc.typeCode === 'DCR')?.id;
        const mucr = payload.goodsShipment?.previousDocument?.find((prevDoc) => prevDoc.typeCode === 'MCR')?.id;

        return {
            ...cdsDeclaration,
            cdsDeclarationPayload: {
                ...payload,
                typeCode,
                additionalTypeCode,
                ducr,
                mucr,
                goodsShipment: {
                    ...payload.goodsShipment,
                    governmentAgencyGoodsItem: cdsDeclaration.cdsGovernmentAgencyGoodsItems?.map((item) =>
                        this.transformProductForClient(item.governmentAgencyGoodsItem)
                    ),
                },
            },
        };
    }

    static transformForServer(_cdsDeclaration: CdsDeclaration) {
        const cdsDeclaration = cloneDeep(_cdsDeclaration);
        const payload = { ...cdsDeclaration.cdsDeclarationPayload };

        delete payload.goodsShipment?.governmentAgencyGoodsItem;

        const typeCode = joinTypeCode(payload.typeCode, payload.additionalTypeCode);
        delete payload.additionalTypeCode;

        delete payload?.ducr;
        delete payload?.mucr;

        return {
            ...cdsDeclaration,
            declarationType: cdsDeclaration.cdsDeclarationPayload?.typeCode,
            additionalDeclarationType: cdsDeclaration.cdsDeclarationPayload?.additionalTypeCode,
            cdsDeclarationPayload: {
                ...payload,
                typeCode,
            },
        };
    }

    static transformProductForClient(_product: CdsGovernmentAgencyGoodsItem | null) {
        const product = cloneDeep(_product);
        let currentCode: string | undefined;
        let previousCode: string | undefined;
        let additionalCurrentCode: (string | undefined)[] = [];

        (product?.governmentProcedure as CdsExportGovernmentProcedureServer)?.forEach((procedure, index) => {
            if (index === 0) {
                currentCode = procedure.currentCode;
                previousCode = procedure.previousCode;
            } else {
                additionalCurrentCode?.push(procedure.currentCode);
            }
        });

        const dangerousGoods = (product?.commodity?.dangerousGoods as any[])?.[0];
        const commodityCodes = transformCommodityCodesForClient(product);
        const additionalDocument = transformAdditionalDocumentForClient(product);

        return {
            ...product,
            governmentProcedure: {
                currentCode,
                previousCode,
                additionalCurrentCode,
            },
            commodity: {
                ...product?.commodity,
                dangerousGoods: dangerousGoods,
                classification: commodityCodes,
            },
            additionalDocument,
        };
    }

    static transformProductForServer(_product: CdsGovernmentAgencyGoodsItem) {
        const product = cloneDeep(_product);
        const additionalCurrentCode = product.governmentProcedure?.additionalCurrentCode?.map(
            (additionalCurrentCode) => ({
                currentCode: additionalCurrentCode,
            })
        );

        let procedures: CdsExportGovernmentProcedureServer = [
            {
                currentCode: product.governmentProcedure?.currentCode,
                previousCode: product.governmentProcedure?.previousCode,
            },
        ];

        if (additionalCurrentCode) {
            procedures = [...procedures, ...additionalCurrentCode];
        }

        const dangerousGoods = [{ undgid: product?.commodity?.dangerousGoods?.undgid }];
        const commodityCodes = transformCommodityCodesForServer(product);
        const additionalDocument = transformAdditionalDocumentForServer(product);

        return {
            ...product,
            governmentProcedure: procedures,
            commodity: {
                ...product?.commodity,
                dangerousGoods: dangerousGoods,
                classification: commodityCodes,
            },
            additionalDocument,
        };
    }
}

export const transformGovernmentProcedurePath = (path: string) => {
    const fieldNameParts = path.split('.');

    const index = fieldNameParts.at(-2);

    fieldNameParts.splice(-2, 1);

    if (index !== '1') {
        fieldNameParts.push((Number(index) - 2).toString());
    }

    return fieldNameParts.join('.');
};

export const transformDangerousGoodsPath = (path: string) => {
    const fieldNameParts = path.split('.');

    if (fieldNameParts.at(-1) !== 'dangerousGoods') {
        fieldNameParts.splice(-2, 1);
    } else {
        fieldNameParts.push('undgid');
    }

    return fieldNameParts.join('.');
};

export const transformAgentPath = (path: string) => {
    return path.replaceAll(/agent/gi, 'representative');
};

export default CdsExportDeclarationUtils;
